import { useTheme } from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import { memo, useMemo } from 'react';

import useCurrentBreakpoint from '@/hooks/useCurrentBreakpoint';
import { resolveCssVariable } from '@/utils/Common.utils';

import { useChartLocationCheck } from '../charts.hooks';
import { getAgChartsTheme } from '../charts.utils';
import RadialGauge from './RadialGauge';
import useStyles from './RadialGaugeChart.styles';
import type { RadialGaugeChartProps } from './RadialGaugeChart.types';

const RadialGaugeChart = memo((props: RadialGaugeChartProps) => {
  const {
    color,
    description,
    limit,
    limitMessage,
    min = 0,
    orientation = 'vertical',
    percent,
    primaryLabel,
    secondaryLabel = '',
    sizeFontsForReports = false,
    stackedDisplay = false,
    title,
    onClick
  } = props;

  const { mode: themeMode } = useColorScheme();
  const currentBreakpoint = useCurrentBreakpoint();
  const theme = useTheme();
  const onReportsPage = useChartLocationCheck();
  // Calculate gradient stops based on limit
  const gradientFills = useMemo(() => {
    if (limit === undefined || limit === 0) {
      // Default gradient stops when no limit is provided
      return [
        { color: '#37924f', stop: 20 },
        { color: '#84ae41', stop: 40 },
        { color: '#dbc42e', stop: 60 },
        { color: '#f07f23', stop: 80 },
        { color: '#ed4a4a', stop: 100 }
      ];
    }

    // Calculate evenly distributed stops for first 4 colors under the limit
    const interval = limit / 4;
    return [
      { color: '#37924f', stop: interval },
      { color: '#84ae41', stop: interval * 2.5 },
      { color: '#dbc42e', stop: interval * 3 },
      { color: '#f07f23', stop: interval * 3.5 },
      { color: '#ed4a4a', stop: limit }
    ];
  }, [limit]);

  const maxLevel = useMemo(() => {
    if (limit && primaryLabel && parseFloat(primaryLabel) <= limit) {
      return limit;
    }
    return percent;
  }, [limit, primaryLabel]);

  // This allows the user to pass in either a string for fixed orientation or
  // an object specifying orientation per breakpoint
  // Responsive orientation based on breakpoint
  const responsiveOrientation = useMemo(() => {
    // If orientation is a string, use it directly
    if (typeof orientation === 'string') {
      return orientation;
    }

    // If orientation is an object with breakpoint-specific values
    const orientationMap = {
      xs: orientation.xs || 'vertical',
      sm: orientation.sm || orientation.xs || 'vertical',
      md: orientation.md || orientation.sm || orientation.xs || 'vertical',
      lg:
        orientation.lg ||
        orientation.md ||
        orientation.sm ||
        orientation.xs ||
        'vertical',
      xl:
        orientation.xl ||
        orientation.lg ||
        orientation.md ||
        orientation.sm ||
        orientation.xs ||
        'vertical'
    };

    return (
      orientationMap[currentBreakpoint as keyof typeof orientationMap] ||
      'vertical'
    );
  }, [orientation, currentBreakpoint]);

  const { classes } = useStyles({
    onClick,
    orientation: responsiveOrientation
  });

  const labelFontSizes = useMemo(() => {
    if (sizeFontsForReports) {
      return { primary: 32, secondary: 16 };
    }

    // Font sizes for stacked/horizontal display (smaller)
    const stackedFontSizeMap = {
      xs: { primary: 20, secondary: 10 },
      sm: { primary: 22, secondary: 11 },
      md: { primary: 20, secondary: 10 },
      lg: { primary: 26, secondary: 13 },
      xl: { primary: 20, secondary: 10 },
      xxl: { primary: 24, secondary: 12 },
      xxxl: { primary: 30, secondary: 15 }
    };

    // Font sizes for regular/vertical display (larger)
    const regularFontSizeMap = {
      xs: { primary: 24, secondary: 12 },
      sm: { primary: 26, secondary: 13 },
      md: { primary: 36, secondary: 18 },
      lg: { primary: 36, secondary: 18 },
      xl: { primary: 36, secondary: 18 },
      xxl: { primary: 30, secondary: 15 },
      xxxl: { primary: 38, secondary: 19 }
    };

    const fontSizeMap = stackedDisplay
      ? stackedFontSizeMap
      : regularFontSizeMap;

    return (
      fontSizeMap[currentBreakpoint as keyof typeof fontSizeMap] ||
      fontSizeMap.xs
    );
  }, [currentBreakpoint, stackedDisplay]);

  const labelText = useMemo(() => {
    if (primaryLabel) {
      return primaryLabel;
    }
    if (typeof percent === 'number') {
      return percent.toFixed(2);
    }
    return '0'; // Return string instead of number
  }, [primaryLabel, percent]);

  const labelColor = useMemo(() => {
    if (sizeFontsForReports) {
      return resolveCssVariable(theme.vars.palette.common.black);
    }

    return themeMode === 'dark'
      ? theme.colorSchemes?.dark?.palette?.text?.primary
      : theme.colorSchemes?.light?.palette?.text?.primary;
  }, [themeMode]);

  const scaleFill = useMemo(() => {
    const darkModeColor = onReportsPage
      ? theme.colorSchemes?.light?.palette?.background?.elevation0
      : theme.colorSchemes?.dark?.palette?.background?.elevation0;
    return themeMode === 'dark'
      ? darkModeColor
      : theme.colorSchemes?.light?.palette?.background?.elevation0;
  }, [themeMode, onReportsPage]);

  const secondaryLabelColor = useMemo(() => {
    if (sizeFontsForReports) {
      return resolveCssVariable(theme.vars.palette.common.black);
    }
    return themeMode === 'dark'
      ? theme.colorSchemes?.dark?.palette?.text?.secondary
      : theme.colorSchemes?.light?.palette?.text?.secondary;
  }, [themeMode]);

  const options = {
    animation: {
      enabled: true
    },
    background: {
      fill: 'transparent'
    },
    bar: {
      ...(color !== undefined && {
        fill: typeof color === 'function' ? color(theme, percent) : color
      }),
      ...(color === undefined && {
        fills: gradientFills,
        fillMode: 'continuous' as const
      })
    },
    cornerRadius: 100,
    label: {
      text: labelText,
      fontSize: labelFontSizes.primary,
      fontWeight: 600,
      color: labelColor || undefined
    },
    padding: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    },
    scale: {
      min,
      max: maxLevel,
      fill: scaleFill || undefined,
      label: {
        enabled: false
      }
    },
    secondaryLabel: {
      text: secondaryLabel,
      fontSize: labelFontSizes.secondary,
      color: secondaryLabelColor || undefined
    },
    segmentation: {
      enabled: true,
      interval: { count: 4 },
      spacing: 3
    },
    theme: getAgChartsTheme(themeMode),
    type: 'radial-gauge' as const,
    value: percent
  };

  return (
    <div className={classes.radialGauge}>
      {percent > 0 && (
        <RadialGauge classes={classes} options={options} onClick={onClick} />
      )}
      {percent > 0 && description && (
        <div className={classes.radialGaugeDescription}>
          <div>
            <span className={classes.radialGaugeDescriptionTitle}>{title}</span>
            {description}
          </div>
        </div>
      )}
      {percent === 0 && limitMessage && <>{limitMessage}</>}
      {percent === 0 && !limitMessage && (
        <div className={classes.radialGaugeNoData}>No data</div>
      )}
    </div>
  );
});

RadialGaugeChart.displayName = 'RadialGaugeChart';

export default RadialGaugeChart;
